import { useState } from "react";
import useHttpHook from "../../../Hooks/HttpHook";
import {
  AcceptAttendeeCancelRequest,
  RejectAttendeeCancelRequest,
} from "../../../utils/ApiConstant";
import { dateFormatter, getAttendeePhotoUrl, getEventImageUrl } from "../../../utils/Helper";
import { DEFAULT_PROFILE_IMAGE } from "../../../utils/brandAssets";
import { OrderLanguage } from "../../../utils/Languages";
import MainComponentWrapper from "../../Common/MainComponentWrapper";
import GalleryModal from "../../Common/Modal/GalleryModal";
import ModalWrapper from "../../Common/Portals/ModalWrapper";
import { toast } from "react-toastify";

const getStatusClassName = (status) => {
  switch (status) {
    case "approved":
    case "refunded":
      return "event-cancel-request-status is-success";
    case "rejected":
      return "event-cancel-request-status is-rejected";
    case "pending":
    default:
      return "event-cancel-request-status is-pending";
  }
};

const PaymentRefund = ({ details, isLoading, isError, getDetails }) => {
  const [reason, setReason] = useState("");
  const [loading, sendRequest, error] = useHttpHook();
  const [isOpen, setIsOpen] = useState(false);
  const [successModel, setSuccessModel] = useState(false);
  const [confirmRejectModal, setConfirmRejectModal] = useState(false);
  const [rejectDetails, setRejectDetails] = useState({
    ticketId: "",
    attendeeId: "",
  });

  const record = Array.isArray(details) ? details[0] : details;
  const event = record?.event;
  const tickets = record?.tickets || [];
  const imagePath = event?.image?.path || event?.image;
  const attendeeCount = tickets.reduce(
    (sum, ticket) => sum + (ticket.attendees?.length || 0),
    0
  );

  const handleAccpectAttendeeCancelRequest = async (ticketId, attendeeId) => {
    try {
      const res = await sendRequest(
        `${AcceptAttendeeCancelRequest}/${ticketId}/${attendeeId}`,
        { method: "get" }
      );
      if (res.status === 1) {
        getDetails();
        setSuccessModel(true);
      }
    } catch (acceptError) {
      console.log(acceptError);
    }
  };

  const handleRejectAttendeeCancelRequest = async () => {
    if (!reason.trim()) {
      toast.error("Please enter a valid reason");
      return;
    }

    try {
      const res = await sendRequest(
        `${RejectAttendeeCancelRequest}/${rejectDetails.ticketId}/${rejectDetails.attendeeId}`,
        {
          method: "POST",
          data: { reason },
        }
      );
      if (res.status === 1) {
        setReason("");
        getDetails();
        setConfirmRejectModal(false);
      }
    } catch (rejectError) {
      console.log(rejectError);
    }
  };

  const renderStatus = (attendee) => {
    if (attendee.cancelRequestStatus === "pending" && attendee.bandId) {
      return (
        <span className="event-cancel-request-status is-rejected">
          Request Expired
        </span>
      );
    }

    return (
      <span className={getStatusClassName(attendee.cancelRequestStatus)}>
        {attendee.cancelRequestStatus || "—"}
      </span>
    );
  };

  return (
    <MainComponentWrapper
      errorInfo={error || isError}
      Loading={loading || isLoading}
    >
      {record?.event && tickets.length > 0 ? (
        <div className="event-payment-refund-body">
          <div className="row g-4 event-payment-refund-top">
            <div className="col-lg-6">
              <div className="event-order-detail-image-wrap">
                <img
                  className="event-order-detail-image"
                  src={imagePath ? getEventImageUrl(imagePath) : ""}
                  alt={event?.title || "Event"}
                  onClick={() => imagePath && setIsOpen(true)}
                />
                {event?.image && (
                  <GalleryModal
                    filesData={[event.image]}
                    Open={isOpen}
                    Index="0"
                    onClose={() => setIsOpen(false)}
                  />
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <article className="event-order-detail-event-card event-payment-refund-event-card">
                <p className="event-payment-refund-date">
                  {dateFormatter(event?.eventTiming?.startDate)}
                </p>
                <h2 className="event-order-detail-event-name">
                  {event?.title || event?.name}
                </h2>
                <dl className="event-payment-refund-meta">
                  <div className="event-payment-refund-meta-row">
                    <dt>Number of Tickets</dt>
                    <dd>{attendeeCount}</dd>
                  </div>
                  <div className="event-payment-refund-meta-row">
                    <dt>Category</dt>
                    <dd>{event?.category || "—"}</dd>
                  </div>
                  {event?.tags?.length > 0 && (
                    <div className="event-payment-refund-meta-row">
                      <dt>Tags</dt>
                      <dd>
                        <div className="event-payment-refund-tags">
                          {event.tags.map((tag, index) => (
                            <span
                              key={`${tag}-${index}`}
                              className="event-payment-refund-tag"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </dd>
                    </div>
                  )}
                </dl>
              </article>
            </div>
          </div>

          <section
            className="event-payment-refund-users"
            aria-label="User information details"
          >
            <h3 className="event-order-detail-section-title">
              User Information Details
            </h3>

            {tickets.map((ticket) => (
              <div key={ticket.id} className="event-payment-refund-ticket-block">
                <h4 className="event-payment-refund-ticket-title">
                  {ticket.name}
                </h4>
                <div className="event-attendees-table-wrap">
                  <table
                    className="event-attendees-table event-payment-refund-table"
                    aria-label={`${ticket.name} attendees`}
                  >
                    <thead>
                      <tr>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.photo}
                        </th>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.name}
                        </th>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.date}
                        </th>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.phone}
                        </th>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.email}
                        </th>
                        <th scope="col">
                          {OrderLanguage.PaymentRedund.list.Columns.status}
                        </th>
                        <th scope="col" className="is-action">
                          {OrderLanguage.PaymentRedund.list.Columns.action}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticket.attendees?.map((attendee, index) => (
                        <tr
                          key={attendee._id || index}
                          className="event-attendees-row"
                        >
                          <td className="event-attendees-cell-profile">
                            <img
                              className="event-attendees-profile"
                              src={getAttendeePhotoUrl(attendee.photo)}
                              alt={attendee.name || ""}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src =
                                  DEFAULT_PROFILE_IMAGE;
                              }}
                            />
                          </td>
                          <td>{attendee.name}</td>
                          <td>{dateFormatter(attendee.dob)}</td>
                          <td>{attendee.mobile}</td>
                          <td>{attendee.email}</td>
                          <td>{renderStatus(attendee)}</td>
                          <td className="event-attendees-cell-actions">
                            {attendee.cancelRequestStatus === "pending" &&
                              !attendee.bandId && (
                                <>
                                  <button
                                    type="button"
                                    className="event-payment-refund-btn is-accept"
                                    onClick={() =>
                                      handleAccpectAttendeeCancelRequest(
                                        ticket.id,
                                        attendee._id
                                      )
                                    }
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="event-payment-refund-btn is-reject"
                                    onClick={() => {
                                      setRejectDetails({
                                        ticketId: ticket.id,
                                        attendeeId: attendee._id,
                                      });
                                      setConfirmRejectModal(true);
                                    }}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            {attendee.cancelRequestStatus === "refunded" && (
                              <span className="event-cancel-request-status is-success">
                                Refunded
                              </span>
                            )}
                            {attendee.cancelRequestStatus === "rejected" && (
                              <span className="event-cancel-request-status is-rejected">
                                Rejected
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>

          {successModel && (
            <ModalWrapper
              title={OrderLanguage.PaymentRedund.refundModel.heading}
              CloseModalHandler={() => {
                setSuccessModel(false);
                getDetails();
              }}
              clickHandler={() => {
                setSuccessModel(false);
                getDetails();
              }}
            >
              <p>{OrderLanguage.PaymentRedund.refundModel.body}</p>
            </ModalWrapper>
          )}

          {confirmRejectModal && (
            <ModalWrapper
              title="Reject Request"
              CloseModalHandler={() => {
                setConfirmRejectModal(false);
                setReason("");
              }}
              clickHandler={handleRejectAttendeeCancelRequest}
              dialogClassName="event-attendees-modal-dialog"
              contentClassName="event-attendees-modal"
              headerClassName="event-attendees-modal-header"
              bodyClassName="event-attendees-modal-body"
              footerClassName="event-attendees-modal-footer"
              closeClass="event-attendees-modal-btn-close"
              submitClass="event-attendees-modal-btn-add"
            >
              <label
                className="event-attendees-modal-label"
                htmlFor="rejectReason"
              >
                Please enter the reason for cancel request
              </label>
              <textarea
                id="rejectReason"
                className="event-attendees-modal-input event-payment-refund-reason"
                name="reason"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </ModalWrapper>
          )}
        </div>
      ) : (
        !isLoading && (
          <div className="event-attendees-empty-state">
            No cancel request details found
          </div>
        )
      )}
    </MainComponentWrapper>
  );
};

export default PaymentRefund;
